/**
 * The two vendor tabs — Airlines and Car Rental — as data, so the list and
 * detail pages are one component each rather than two near-copies.
 *
 * Source of the rows: hsp.vendor_master, synced daily from Andavo's own vendor
 * table (Domo `Andavo | Vendor`). Never AirPortal. See
 * customer-management-api/src/lib/hsp/vendor-master.ts.
 *
 * `type` is what the API filters on, and each tab asks for exactly one: codes
 * collide across types (ZE is Hertz AND Eastar Jet), so a mixed list would show
 * two companies under one code.
 */

export type VendorType = 'air' | 'car';

export interface VendorKind {
  type: VendorType;
  /** The tab's route, and the prefix of every detail link. */
  path: '/hotel-supplier/airlines' | '/hotel-supplier/car-rental';
  title: string;
  /** Singular, lower case, for sentences: "No airline matches…". */
  noun: string;
  /** What the code column is called for this kind. */
  codeLabel: string;
  altCodeLabel: string;
  /** Names the fields the server actually searches: name, code, alternate code. */
  searchPlaceholder: string;
  intro: string;
  testId: string;
}

export const VENDOR_KINDS: Record<VendorType, VendorKind> = {
  air: {
    type: 'air',
    path: '/hotel-supplier/airlines',
    title: 'Airlines',
    noun: 'airline',
    codeLabel: 'IATA code',
    altCodeLabel: 'ICAO code',
    searchPlaceholder: 'Search name, IATA or ICAO code…',
    intro:
      'Every airline in Andavo’s vendor list, with the contacts and contracts ' +
      'Vendor Relations keeps on each — corporate discounts, TMC deals, tour ' +
      'codes and ticket designators, with the signed PDF.',
    testId: 'vendors-air',
  },
  car: {
    type: 'car',
    path: '/hotel-supplier/car-rental',
    title: 'Car Rental',
    noun: 'car rental company',
    codeLabel: 'Vendor code',
    altCodeLabel: 'Alternate code',
    searchPlaceholder: 'Search name or vendor code…',
    intro:
      'Every rental car company in Andavo’s vendor list, with the contacts and ' +
      'contracts Vendor Relations keeps on each — corporate discounts, CDP and ' +
      'discount codes, with the signed PDF.',
    testId: 'vendors-car',
  },
};

/** One vendor as GET /api/btp/vendors and /api/btp/vendors/:uid return it. */
export interface Vendor {
  uid: string;
  service_type: VendorType;
  code: string;
  code_alternate: string | null;
  name: string;
  is_active: boolean;
  website_url: string | null;
  source_updated_at: string | null;
  synced_at: string;
  removed_from_source_at: string | null;
}

export interface VendorListRow extends Vendor {
  primary_contact_name: string | null;
  live_contracts: number;
}

export interface VendorListResult {
  vendors: VendorListRow[];
  total: number;
  page: number;
  page_size: number;
  counts: {
    active: number;
    inactive: number;
    all: number;
    /** Same status + search, narrowed to vendors with a live contract. */
    with_contracts: number;
  };
}

export function isVendorType(v: string | undefined): v is VendorType {
  return v === 'air' || v === 'car';
}
